import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { ClipboardPaste, Loader2, X, ScanText, Save, Trash2, Check, Minus, Landmark } from 'lucide-react';
import { IIBulkImport } from './IIBulkImport';

const PICK = ['firm', 'regions', 'strategies', 'sectors', 'deals', 'funds', 'people', 'offices'];

function Shots({ shots, onRemove }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="ii-shots">
      {shots.map((s, i) => (
        <div key={s.url} className="relative group w-28 h-16 rounded-sm overflow-hidden border border-border bg-black/40" data-testid={`ii-shot-${i}`}>
          <img src={s.url} alt="" className="w-full h-full object-cover object-top" />
          <span className="absolute bottom-0 left-0 right-0 text-[9px] text-center bg-black/60 text-foreground/80">#{i + 1}</span>
          <button onClick={() => onRemove(i)} className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/70 text-white hidden group-hover:flex items-center justify-center" data-testid={`ii-shot-remove-${i}`}><X className="w-3 h-3" /></button>
        </div>
      ))}
    </div>
  );
}

function Checks({ label, items }) {
  if (!items?.length) return null;
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">{label}</p>
      <div className="flex flex-wrap gap-1">
        {items.map((x, i) => (
          <span key={i} className={`inline-flex items-center gap-1 text-[10px] rounded-sm px-1.5 py-px border ${x.checked ? 'text-emerald-200 border-emerald-500/40 bg-emerald-500/10' : 'text-muted-foreground/60 border-border line-through'}`}>
            {x.checked ? <Check className="w-2.5 h-2.5" /> : <Minus className="w-2.5 h-2.5" />}{x.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function MiniTable({ title, rows, cols, testid }) {
  if (!rows?.length) return null;
  const dupes = rows.filter(r => r.duplicate).length;
  return (
    <div data-testid={testid}>
      <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">
        {title} <span className="text-foreground">{rows.length}</span>{dupes ? <span className="text-muted-foreground/70"> · {dupes} bereits vorhanden</span> : ''}
      </p>
      <div className="max-h-56 overflow-auto border border-border rounded-sm">
        <table className="w-full text-[11px]">
          <tbody className="divide-y divide-border/40">
            {rows.map((r, i) => (
              <tr key={i} className={r.duplicate ? 'opacity-50' : ''}>
                {cols.map(c => <td key={c} className="px-2 py-1 align-top whitespace-nowrap max-w-[260px] truncate">{r[c] || <span className="text-muted-foreground/40">—</span>}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Preview({ data, fixedFund, onChangeName, onSave, onDiscard, saving }) {
  const firm = data.firm;
  const target = fixedFund || data.match;
  const newDeals = data.deals.filter(d => !d.duplicate).length;
  return (
    <div className="space-y-3" data-testid="ii-preview">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Investor</label>
          {fixedFund ? <p className="text-sm font-medium text-foreground" data-testid="ii-firm-name">{fixedFund.name}</p> : (
            <input value={firm.name} onChange={e => onChangeName(e.target.value)} data-testid="ii-firm-name-input"
              className="w-full bg-secondary border border-border rounded-sm px-2 h-8 text-sm text-foreground focus:outline-none focus:border-amber-500/60" placeholder="Firmenname (aus Overview-Screenshot)" />
          )}
          <p className="text-[11px] mt-1" data-testid="ii-target-note">
            {target ? <span className="text-sky-300">Wird dem bestehenden Fonds „{target.name}“ hinzugefügt (ergänzt, nichts wird gelöscht)</span>
              : <span className="text-amber-300">Neuer Fonds „{firm.name || '…'}“ wird angelegt — ohne KI-Recherche, die startest du später</span>}
          </p>
        </div>
        <div className="grid grid-cols-3 gap-2 text-[11px]">
          {[['Headquarters', firm.headquarters], ['Website', firm.website], ['AUM', firm.aum]].map(([k, v]) => (
            <div key={k} className="bg-secondary/60 border border-border rounded-sm px-2 py-1 min-w-[110px]"><p className="text-[9px] text-muted-foreground">{k}</p><p className="text-foreground truncate">{v || '—'}</p></div>
          ))}
        </div>
      </div>
      {firm.about && <p className="text-[11px] text-muted-foreground leading-snug line-clamp-3" data-testid="ii-about">{firm.about}</p>}
      <div className="grid sm:grid-cols-3 gap-3">
        <Checks label="Regions" items={data.regions} /><Checks label="Strategies" items={data.strategies} /><Checks label="Sectors" items={data.sectors} />
      </div>
      <MiniTable title="Deals" rows={data.deals} cols={['headline', 'fund_vehicle', 'value', 'status', 'date', 'sector', 'region']} testid="ii-deals" />
      <div className="grid lg:grid-cols-2 gap-3">
        <MiniTable title="Funds" rows={data.funds} cols={['name', 'kind', 'deals_count', 'size', 'vintage', 'strategy']} testid="ii-funds" />
        <MiniTable title="People" rows={data.people} cols={['name', 'title', 'city', 'email']} testid="ii-people" />
      </div>
      <MiniTable title="Offices" rows={data.offices} cols={['country', 'city', 'address', 'phone']} testid="ii-offices" />
      <div className="flex items-center gap-2 flex-wrap pt-1">
        <button onClick={onSave} disabled={saving || (!target && !firm.name?.trim())} data-testid="ii-save-btn"
          className="inline-flex items-center gap-1.5 text-xs font-medium rounded-sm px-4 h-9 bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-50">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Speichern · {newDeals} Deals · {data.funds.length} Funds · {data.people.length} Kontakte
        </button>
        <button onClick={onDiscard} className="inline-flex items-center gap-1.5 text-xs rounded-sm px-3 h-9 border border-border text-muted-foreground hover:text-foreground" data-testid="ii-discard-btn">
          <Trash2 className="w-3.5 h-3.5" /> Verwerfen
        </button>
        <span className="text-[10px] text-muted-foreground/70">{data.screenshots} Screenshots gelesen{data.errors?.length ? ` · ${data.errors.length} nicht lesbar` : ''}</span>
      </div>
    </div>
  );
}

export function IIImportPanel({ fund, onSaved, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const [mode, setMode] = useState('single');
  const [shots, setShots] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [last, setLast] = useState(null);
  const inputRef = useRef(null);
  const openRef = useRef(open);
  openRef.current = open && mode === 'single';

  const addFiles = useCallback((files) => {
    const imgs = [...files].filter(f => f.type.startsWith('image/'));
    if (!imgs.length) return;
    setShots(prev => [...prev, ...imgs.map((f, i) => ({ file: new File([f], f.name && f.name !== 'image.png' ? f.name : `screenshot-${prev.length + i + 1}.png`, { type: f.type }), url: URL.createObjectURL(f) }))]);
    setData(null); setLast(null);
  }, []);

  useEffect(() => {
    const h = (e) => {
      if (!openRef.current) return;
      const items = [...(e.clipboardData?.items || [])].filter(i => i.type.startsWith('image/'));
      if (!items.length) return;
      e.preventDefault();
      addFiles(items.map(i => i.getAsFile()).filter(Boolean));
    };
    window.addEventListener('paste', h);
    return () => window.removeEventListener('paste', h);
  }, [addFiles]);

  const reset = () => { shots.forEach(s => URL.revokeObjectURL(s.url)); setShots([]); setData(null); };

  const extract = async () => {
    setBusy(true);
    try {
      const r = await api.iiExtract(shots.map(s => s.file), fund?.id);
      setData(r.data);
      if (r.data.errors?.length) toast.warning(r.data.errors.join(' · '));
    } catch (e) { toast.error(e.response?.data?.detail || 'Auslesen fehlgeschlagen'); }
    setBusy(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = Object.fromEntries(PICK.map(k => [k, data[k]]));
      const r = await api.iiSave({ ...body, fund_id: fund?.id || data.match?.id || '', screenshots: data.screenshots });
      const res = r.data;
      toast.success(`${res.created ? 'Neuer Fonds' : 'Fonds'} „${res.fund.name}“ gespeichert — ${res.deals_saved} Deals, ${res.funds} Funds, ${res.people} Kontakte`);
      setLast(res);
      reset();
      onSaved?.(res);
    } catch (e) { toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    setSaving(false);
  };

  return (
    <div className="bg-card border border-border rounded-sm" data-testid="ii-import-panel">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left" data-testid="ii-import-toggle">
        <div>
          <p className="text-[10px] uppercase tracking-widest font-bold text-amber-300/80 inline-flex items-center gap-1.5"><ClipboardPaste className="w-3 h-3" /> Infrastructure-Investor-Profil einfügen</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {fund ? `Screenshots der II-Seite von ${fund.name} (weitere Deal-Seiten, Funds, Contacts) einfügen — ergänzt das Profil.`
              : 'Screenshots der II-Profilseite (Overview, alle Deal-Seiten, Funds, Contacts) mit Strg+V hier einfügen — die KI liest Fakten, Deals, Fonds und Kontakte aus und legt den Fonds an.'}
          </p>
        </div>
        <span className="text-[11px] text-muted-foreground shrink-0">{open ? 'zuklappen' : 'öffnen'}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          {!fund && (
            <div className="flex items-center gap-1 text-[11px]" data-testid="ii-mode">
              {[['single', 'Einzelne Firma · mit Vorschau'], ['bulk', 'Massen-Import · hunderte Screenshots, ohne Vorschau']].map(([k, label]) => (
                <button key={k} onClick={() => setMode(k)} data-testid={`ii-mode-${k}`}
                  className={`rounded-sm px-2.5 h-7 border ${mode === k ? 'border-amber-500/60 text-amber-300 bg-amber-500/10' : 'border-border text-muted-foreground hover:text-foreground'}`}>{label}</button>
              ))}
            </div>
          )}
          {mode === 'bulk' && !fund ? <IIBulkImport onSaved={onSaved} /> : (
          <>
          {!data && (
            <div onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files); }} onClick={() => !shots.length && inputRef.current?.click()}
              data-testid="ii-dropzone"
              className={`border border-dashed rounded-sm p-4 transition-colors ${shots.length ? 'border-border' : 'border-amber-500/40 hover:border-amber-400 cursor-pointer'}`}>
              {shots.length ? <Shots shots={shots} onRemove={(i) => setShots(prev => prev.filter((_, j) => j !== i))} /> : (
                <p className="text-sm text-muted-foreground text-center py-3 inline-flex items-center gap-2 w-full justify-center">
                  <ClipboardPaste className="w-4 h-4 text-amber-300" /> Strg+V / ⌘V — Screenshot einfügen, oder hierher ziehen bzw. klicken. Mehrere Seiten nacheinander einfügen.
                </p>
              )}
              <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={e => { addFiles(e.target.files); e.target.value = ''; }} data-testid="ii-file-input" />
            </div>
          )}
          {!data && shots.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={extract} disabled={busy} data-testid="ii-extract-btn"
                className="inline-flex items-center gap-1.5 text-xs font-medium rounded-sm px-4 h-9 bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-50">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ScanText className="w-3.5 h-3.5" />} {busy ? 'KI liest Screenshots…' : `${shots.length} Screenshot${shots.length > 1 ? 's' : ''} auslesen`}
              </button>
              <button onClick={() => inputRef.current?.click()} className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2" data-testid="ii-add-more-btn">weitere hinzufügen</button>
              <button onClick={reset} className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2" data-testid="ii-clear-btn">leeren</button>
              <span className="text-[10px] text-muted-foreground/70">~20–40 s pro Screenshot · Gemini Vision</span>
            </div>
          )}
          {data && (
            <Preview data={data} fixedFund={fund} saving={saving} onSave={save} onDiscard={reset}
              onChangeName={(v) => setData(d => ({ ...d, firm: { ...d.firm, name: v }, match: null }))} />
          )}
          {last && !data && !shots.length && (
            <p className="text-[11px] text-emerald-300 inline-flex items-center gap-1.5" data-testid="ii-last-saved">
              <Landmark className="w-3 h-3" /> „{last.fund.name}“ gespeichert ({last.deals_saved} Deals, {last.funds} Funds, {last.people} Kontakte)
              {!fund && <a href={`/infra-funds/${last.fund.id}`} className="underline underline-offset-2 ml-1">öffnen</a>} — nächste Firma: Screenshots einfügen.
            </p>
          )}
          </>
          )}
        </div>
      )}
    </div>
  );
}
